import { UserIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Pagination from "../../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import { buildApiUrl, formatDate } from "../../../lib/utils";
import {
  VehicleAssignment,
  VehicleAssignmentHistorySearchRequest,
  VehicleAssignmentSearchResponse,
} from "../@types";
import { useGetAssignmentHistoryMutation } from "../vehicleApiSlice";

interface VehicleAssignmentHistoryProps {
  id: string;
  idType?: "vehicleId" | "driverId";
}

const VehicleAssignmentHistory = ({
  id,
  idType = "vehicleId",
}: VehicleAssignmentHistoryProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // RTK Query mutation
  const [getAssignmentHistory, { data: assignmentData, isLoading }] =
    useGetAssignmentHistoryMutation();

  const fetchAssignments = useCallback(async () => {
    const requestBody: VehicleAssignmentHistorySearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      vehicleId: idType === "vehicleId" ? id : undefined,
      driverId: idType === "driverId" ? id : undefined,
    };

    try {
      await getAssignmentHistory(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch assignments history:", err);
    }
  }, [currentPage, pageSize, getAssignmentHistory, id]);

  // Fetch assignments on component mount and when filters change
  useEffect(() => {
    fetchAssignments();
  }, [currentPage, pageSize, fetchAssignments]);

  // Table columns configuration
  const columns: Column<VehicleAssignment>[] = [
    {
      key: "vehicleFormatedId",
      header: "Vehicle ID",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "driverFullName",
      header: "Driver",
      width: "",
      render: (value: string, item: VehicleAssignment) => (
        <div className="flex items-center space-x-3">
          {item.driverImgUrl ? (
            <img
              src={buildApiUrl(item.driverImgUrl)}
              alt={`${item.driverFullName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{value}</span>
          </div>
        </div>
      ),
    },
    {
      key: "vehicleName",
      header: "Vehicle",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "targetJourneys",
      header: "Target Journeys",
      width: "16",
      render: (value: number) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "finalJourneys",
      header: "Final Journeys",
      width: "16",
      render: (value: number) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "20",
      render: (value: string) => {
        const getStatusInfo = () => {
          if (value === "Completed")
            return {
              label: "Completed",
              color: "text-green-800 border border-green-800",
            };
          if (value === "Active")
            return {
              label: "Active",
              color: "text-blue-800 border border-blue-800",
            };
          if (value === "Pending")
            return {
              label: "Pending",
              color: "text-yellow-800 border border-yellow-800",
            };
          return {
            label: "Inactive",
            color: "text-gray-200 border border-gray-200",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`px-2 py-1 inline-flex items-center justify-center text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "startDate",
      header: "Start Date",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },
    {
      key: "endDate",
      header: "End Date",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{value ? formatDate(value) : "N/A"}</span>
      ),
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const response = assignmentData?.data as
    | VehicleAssignmentSearchResponse
    | undefined;

  return (
    <div className="bg-white rounded-[10px] p-4">
      {/* Table */}
      <div className="overflow-hidden space-y-6">
        <Table
          data={response?.data || []}
          columns={columns}
          loading={isLoading}
          emptyMessage="No assignments found"
        />

        {/* Pagination */}
        {response && (
          <Pagination
            currentPage={response.currentPage}
            totalPages={response.totalPages}
            pageSize={response.pageSize}
            totalCount={response.totalCount}
            hasNextPage={response.hasNextPage}
            hasPreviousPage={response.hasPreviousPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </div>
  );
};

export default VehicleAssignmentHistory;
